/**
 * Text that has to survive as ONE line.
 *
 * WhatsApp refuses a line break inside a template variable: the message body
 * the portal sends to a passenger goes into the `{{2}}` slot of a three-block
 * template, and a single newline anywhere in it makes the whole send fail.
 * That is a constraint of the transport, not of the person typing, so the
 * portal absorbs it rather than relaying it as a validation error.
 *
 * TWO functions, because a composer needs both and they are not the same job.
 * `flattenLines` is what runs WHILE SOMEBODY IS TYPING — it takes the line
 * breaks out and touches nothing else, so a pasted paragraph becomes one line
 * silently and the space just pressed between two words is still there when
 * the next one is typed. `oneLine` is the WIRE format, applied once on send:
 * every run of whitespace becomes a single space and the ends are trimmed.
 *
 * Collapsing runs on every keystroke instead would eat the trailing space of
 * every word typed and spell "helloworld".
 */

/**
 * Every whitespace character EXCEPT a plain space — a newline, a carriage
 * return, a tab, a vertical tab, a form feed and the Unicode line and
 * paragraph separators. Written as "not a non-space, and not a space" so the
 * class needs no \u escapes: U+2028 is itself a line terminator, and spelling
 * it literally inside a regex literal would end the literal.
 */
const LINE_BREAKS = /[^\S ]+/g;

/** Line breaks → a single space. Plain spaces are untouched, nothing is trimmed. */
export const flattenLines = (value) => String(value ?? '').replace(LINE_BREAKS, ' ');

/** The one-line form that goes on the wire: whitespace runs collapsed, ends trimmed. */
export const oneLine = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

/** Whether a string carries anything a single-line field would have to flatten. */
export const hasLineBreak = (value) => /[^\S ]/.test(String(value ?? ''));

/**
 * The value a field should end up with when a multi-line paste lands in it.
 *
 * WHY A PASTE CANNOT BE LEFT TO THE BROWSER. `<input type="text">` runs the
 * HTML value-sanitization algorithm, which STRIPS carriage returns and line
 * feeds rather than replacing them. So pasting
 *
 *     Your driver is here.
 *     He is at exit B.
 *
 * leaves "Your driver is here.He is at exit B." — the words welded together,
 * which is worse than either the paragraph or a clean single line, and it
 * happens before any `onChange` handler can see the newline. The field has to
 * intercept the paste itself to get a space in there.
 *
 * `start`/`end` are the field's selection, so a paste REPLACES whatever was
 * highlighted, exactly as a native one would. Omitted, it appends.
 */
export const pasteOneLine = ({
  value = '', pasted = '', start = null, end = null,
} = {}) => {
  const text = String(value ?? '');
  const from = Number.isInteger(start) ? start : text.length;
  const to = Number.isInteger(end) ? end : from;

  return flattenLines(text.slice(0, from) + String(pasted ?? '') + text.slice(to));
};

export default oneLine;
